import { MUI_MENU_ITEM, MUI_TAB, MUI_TOOLTIP } from "@constants/mui.constants";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

const theme = createTheme({
	typography: {
		fontFamily: '"Poppins", sans-serif',
		// Override specific typography variants to ensure Poppins is used
		h1: {
			fontFamily: '"Poppins", sans-serif',
		},
		h2: {
			fontFamily: '"Poppins", sans-serif',
		},
		h3: {
			fontFamily: '"Poppins", sans-serif',
		},
		h4: {
			fontFamily: '"Poppins", sans-serif',
		},
		h5: {
			fontFamily: '"Poppins", sans-serif',
		},
		h6: {
			fontFamily: '"Poppins", sans-serif',
		},
		body1: {
			fontFamily: '"Poppins", sans-serif',
		},
		body2: {
			fontFamily: '"Poppins", sans-serif',
		},
		button: {
			fontFamily: '"Poppins", sans-serif',
		},
		caption: {
			fontFamily: '"Poppins", sans-serif',
		},
		overline: {
			fontFamily: '"Poppins", sans-serif',
		},
	},
	components: {
		MuiMenuItem: {
			styleOverrides: {
				root: {
					"&.Mui-selected": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.BACKGROUND_COLOR, // primary.main
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.HOVER_COLOR, // custom hover
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
					"&:hover": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.HOVER_COLOR, // custom hover
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: {},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					background: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
					color: MUI_TOOLTIP.TOOLTIP_COLOR,
					boxShadow: MUI_TOOLTIP.TOOLTIP_BOX_SHADOW,
					borderRadius: MUI_TOOLTIP.TOOLTIP_BORDER_RADIUS,
					fontSize: MUI_TOOLTIP.TOOLTIP_FONT_SIZE,
					padding: MUI_TOOLTIP.TOOLTIP_PADDING,
					letterSpacing: MUI_TOOLTIP.TOOLTIP_LETTER_SPACING,
					fontFamily: MUI_TOOLTIP.TOOLTIP_FONT_FAMILY,
				},
				arrow: {
					color: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					"& .MuiTabs-indicator": {
						backgroundColor: MUI_TAB.SELECTED.INDICATOR_COLOR,
						height: 4,
						borderRadius: 2,
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: "capitalize",
					fontSize: 12,
					borderRadius: 8,
					minHeight: 40,
					marginRight: 8,
					transition: "background 0.2s, color 0.2s",
					"&.Mui-selected": {
						color: MUI_TAB.SELECTED.TAB_NAME_COLOR,
						backgroundColor: MUI_TAB.SELECTED.BACKGROUND_COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
					"&:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
				},
			},
		},
		// Ensure all MUI components use Poppins
		MuiButton: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", sans-serif',
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					"& .MuiInputBase-root": {
						fontFamily: '"Poppins", sans-serif',
					},
					"& .MuiInputLabel-root": {
						fontFamily: '"Poppins", sans-serif',
					},
					"& .MuiInputBase-input::placeholder": {
						color: "rgba(0, 0, 0, 0.38)",
						opacity: 1,
					},
					"& input::placeholder": {
						color: "rgba(0, 0, 0, 0.38)",
						opacity: 1,
					},
					// Date input specific styling - light gray placeholder, dark when filled
					"& input[type='date']::-webkit-datetime-edit-fields-wrapper": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					"& input[type='date']::-webkit-datetime-edit": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					"& input[type='date']::-webkit-datetime-edit-text": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					"& input[type='date']::-webkit-datetime-edit-month-field": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					"& input[type='date']::-webkit-datetime-edit-day-field": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					"& input[type='date']::-webkit-datetime-edit-year-field": {
						color: "rgba(0, 0, 0, 0.38)",
					},
					// Dark color when date is filled
					"& input[type='date'].Mui-focused::-webkit-datetime-edit": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'].Mui-focused::-webkit-datetime-edit-text": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'].Mui-focused::-webkit-datetime-edit-month-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'].Mui-focused::-webkit-datetime-edit-day-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'].Mui-focused::-webkit-datetime-edit-year-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					// Dark color when date has value
					"& input[type='date'][value]:not([value=''])::-webkit-datetime-edit": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'][value]:not([value=''])::-webkit-datetime-edit-text": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'][value]:not([value=''])::-webkit-datetime-edit-month-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'][value]:not([value=''])::-webkit-datetime-edit-day-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
					"& input[type='date'][value]:not([value=''])::-webkit-datetime-edit-year-field": {
						color: "rgba(0, 0, 0, 0.87)",
					},
				},
			},
		},
		MuiDialog: {
			styleOverrides: {
				root: {
					"& .MuiDialogTitle-root": {
						fontFamily: '"Poppins", sans-serif',
					},
					"& .MuiDialogContent-root": {
						fontFamily: '"Poppins", sans-serif',
					},
				},
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					"& .MuiTableCell-root": {
						fontFamily: '"Poppins", sans-serif',
					},
					"& .MuiTableHead-root .MuiTableCell-root": {
						fontFamily: '"Poppins", sans-serif',
					},
				},
			},
		},
	},
});

export function MUIThemeProvider({ children }: { children: ReactNode }) {
	return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
